import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export interface Profile {
  id: string;
  display_name: string | null;
  avatar_url: string | null;
  last_display_name_change: string | null;
  created_at: string;
  updated_at: string;
}

export function useProfile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchProfile();
    } else {
      setProfile(null);
      setLoading(false);
    }
  }, [user]);

  const fetchProfile = async () => {
    if (!user) return;
    
    setLoading(true);
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .maybeSingle();
    
    if (error) {
      console.error('Error fetching profile:', error);
    } else {
      setProfile(data);
    }
    setLoading(false);
  };

  const updateProfile = async (updates: Partial<Pick<Profile, 'display_name' | 'avatar_url'>>) => {
    if (!user) return { error: new Error('Not authenticated') };

    const { error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', user.id);

    if (error) {
      toast.error('Failed to update profile');
      return { error };
    }

    setProfile(prev => prev ? { ...prev, ...updates } : null);
    toast.success('Profile updated');
    return { error: null };
  };

  const updateDisplayName = async (newName: string) => {
    if (!user) return { error: new Error('Not authenticated') };

    // Check if name is available
    const { data: isAvailable } = await supabase.rpc('is_display_name_available', { name: newName });
    
    if (!isAvailable) {
      return { error: new Error('Display name is already taken') };
    }

    // Check cooldown
    const { data: canChange } = await supabase.rpc('can_change_display_name', { target_user_id: user.id });
    
    if (!canChange) {
      const { data: daysLeft } = await supabase.rpc('days_until_display_name_change', { target_user_id: user.id });
      return { error: new Error(`You can change your display name again in ${daysLeft} days`) };
    }

    // Update display name and set the timestamp
    const { error } = await supabase
      .from('profiles')
      .update({ 
        display_name: newName, 
        last_display_name_change: new Date().toISOString() 
      })
      .eq('id', user.id);

    if (error) {
      return { error };
    }

    setProfile(prev => prev ? { 
      ...prev, 
      display_name: newName, 
      last_display_name_change: new Date().toISOString() 
    } : null);
    
    toast.success('Display name updated');
    return { error: null };
  };

  const checkCanChangeDisplayName = async (): Promise<{ canChange: boolean; daysLeft: number }> => {
    if (!user) return { canChange: false, daysLeft: 0 };
    
    const { data: canChange } = await supabase.rpc('can_change_display_name', { target_user_id: user.id });
    const { data: daysLeft } = await supabase.rpc('days_until_display_name_change', { target_user_id: user.id });
    
    return { canChange: canChange ?? true, daysLeft: daysLeft ?? 0 };
  };

  const uploadAvatar = async (file: File) => {
    if (!user) return { error: new Error('Not authenticated'), url: null };

    const fileExt = file.name.split('.').pop();
    const filePath = `${user.id}/${Date.now()}.${fileExt}`;

    // Upload file
    const { error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(filePath, file, { upsert: true });

    if (uploadError) {
      toast.error('Failed to upload avatar');
      return { error: uploadError, url: null };
    }

    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from('avatars')
      .getPublicUrl(filePath);

    // Update profile with new avatar URL
    await updateProfile({ avatar_url: publicUrl });

    return { error: null, url: publicUrl };
  };

  return {
    profile,
    loading,
    updateProfile,
    updateDisplayName,
    checkCanChangeDisplayName,
    uploadAvatar,
    refetch: fetchProfile,
  };
}
